import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Eye, EyeOff } from 'lucide-react';

/**
 * Reusable password modal for unlocking PDFs.
 *
 * Props:
 *   open — boolean
 *   title — string, modal title (defaults to "PDF is password-protected")
 *   description — string, body text below title
 *   attemptsRemaining — number; if 0, modal shows the "failed" state and only "Cancel" works
 *   onSubmit — (password: string) => void
 *   onCancel — () => void
 */
export default function PdfPasswordModal({
  open, title, description, attemptsRemaining = 3, onSubmit, onCancel,
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setShow(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4 bg-gray-900/40">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md p-6">
        <div className="flex items-center gap-2 mb-2">
          <Lock size={18} className="text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            {title || t('common:password.title', 'PDF is password-protected')}
          </h2>
        </div>
        {description && <p className="text-sm text-gray-600 mb-4">{description}</p>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (password && attemptsRemaining > 0) onSubmit(password);
          }}
        >
          <div className="relative">
            <input
              ref={inputRef}
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
              placeholder={t('common:password.placeholder', 'Enter password')}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600"
              aria-label={show ? 'Hide password' : 'Show password'}
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {attemptsRemaining < 3 && attemptsRemaining > 0 && (
            <p className="mt-2 text-xs text-amber-600">
              {t('common:password.attempts', '{{n}} attempts remaining', { n: attemptsRemaining })}
            </p>
          )}
          {attemptsRemaining === 0 && (
            <p className="mt-2 text-xs text-red-600">
              {t('common:password.exhausted', 'No attempts remaining. Cancel and try a different file.')}
            </p>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              type="submit"
              disabled={!password || attemptsRemaining === 0}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {t('common:buttons.confirm', 'Confirm')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
